import * as React from 'react';
import { memo } from 'react';

import styled from 'styled-components';

import { Menu } from 'organisms';

import { Colors } from 'services/ColorService';
import { LAYERS } from 'components/constants';

export interface IMainTemplate {
  children: React.ReactElement;
  /**
   * Full-bleed, full-viewport-height variant: drops the 1312px content cap and
   * lets the child fill the space under the menu (used by the Communicator so
   * it can render as an edge-to-edge, full-height messenger). Off by default —
   * every other page keeps the centered, capped layout.
   */
  fluid?: boolean;
}

/** Ширина листа страницы и его поля на узком окне. */
const PAGE_MAX = 1312;
const PAGE_GUTTER = 16;

export const MainTemplate = memo(({ children, fluid }: IMainTemplate) => (
  <Paper $fluid={fluid}>
    <MenuWrapper>
      <MenuMargin $fluid={fluid}>
        <Menu />
      </MenuMargin>
    </MenuWrapper>
    <ContentWrapper $fluid={fluid}>{children}</ContentWrapper>
  </Paper>
));

/**
 * Лист страницы.
 *
 * 🔴 Нижнего зазора здесь НЕТ и быть не должно — владелец сказал прямо. Раньше
 * его добавлял каждый список сам: девять таблиц несли `margin-bottom: 60px`,
 * четыре страницы — `margin: 32px 0 64px`, плюс финансы, опросы и Zoom. Все
 * они убраны, и заводить общий взамен не надо: страница кончается там, где
 * кончается содержимое.
 *
 * Почему это было не только некрасиво: зазор лежал ВНУТРИ листа, а у листа
 * `min-height: 100vh`. Стоило содержимому дорасти до нижней кромки экрана, как
 * 60 пикселей пустоты выталкивали документ за 100vh — появлялась полоса
 * прокрутки, под которой ничего нет.
 *
 * `box-sizing: border-box` оставлен намеренно: если сюда когда-нибудь вернут
 * padding, он будет считаться ВНУТРИ 100vh и той же полосы прокрутки не даст.
 */
export const Paper = styled.div<{ $fluid?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: ${({ $fluid }) => ($fluid ? '100vh' : 'auto')};
  min-height: 100vh;
  overflow: ${({ $fluid }) => ($fluid ? 'hidden' : 'visible')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: ${Colors.MainBackground};
`;

/**
 * Поля по бокам листа, пока окно уже листа (1312 + 2 × 16): иначе шапка и
 * страница упираются в края окна. Шире — лист по центру, поля не нужны.
 */
const NARROW_GUTTER = `@media (max-width: ${PAGE_MAX + 2 * PAGE_GUTTER - 1}px)`;

const MenuMargin = styled.div<{ $fluid?: boolean }>`
  max-width: ${({ $fluid }) => ($fluid ? 'none' : `${PAGE_MAX}px`)};
  padding: ${({ $fluid }) => ($fluid ? '0 24px' : '0')};
  box-sizing: border-box;
  margin: 0 auto;

  ${NARROW_GUTTER} {
    padding: ${({ $fluid }) => ($fluid ? '0 24px' : `0 ${PAGE_GUTTER}px`)};
  }
`;

/**
 * Полоса шапки с главным меню.
 *
 * 🔴 `position: relative` + `z-index` обязательны, и вот почему. Выпадающий
 * список меню — не портал: это `<ul position: absolute>` внутри `<li>`, у
 * которого стоит `z-index: 1` (`molecules/MenuItem`). Этот `z-index` создаёт
 * stacking context, то есть запирает весь список на уровне `1`, и поднять его
 * собственным числом на `<ul>` НЕВОЗМОЖНО — число разрешается внутри контекста
 * `<li>`. Пока шапка была `static`, меню оказывалось под любым содержимым
 * страницы: липкой шапкой Ганта (4), заголовками таблиц Cash Flow (2-3),
 * подсказками (30), пунктами уроков (100) — а при равном `z-index: 1` просто
 * потому, что содержимое идёт позже в DOM.
 *
 * Поднимаем именно ЭТУ обёртку, а не `<li>` и не `<ul>`: так весь заголовок
 * целиком встаёт над страницей одним слоем, и внутренние числа меню перестают
 * иметь значение.
 */
const MenuWrapper = styled.div`
  position: relative;
  z-index: ${LAYERS.HEADER};
  width: 100%;
  background-color: ${Colors.White};
  border-bottom: 1px solid ${Colors.LightGray};
`;

/**
 * Содержимое страницы.
 *
 * 🔴 `isolation: isolate` — то, что закрывает КЛАСС дефекта, а не один случай.
 * Он делает содержимое отдельным stacking context, поэтому любой `z-index`
 * внутри страницы (сегодняшний и будущий, хоть `9999`) разрешается ВНУТРИ неё и
 * физически не может всплыть над шапкой. Без него каждый новый экран, написавший
 * себе число побольше, снова накрывал бы меню — так это и происходило годами:
 * лечили симптом, опуская содержимое (см. историю правки в `PaymentCalendar`).
 *
 * Накладки это не задевает: `GlobalToast`, `GlobalModal` и `CommandPalette`
 * монтируются в `router/RootRouter`, то есть ВНЕ этого узла, а поповеры antd
 * уходят в `document.body`. Запереть их изоляцией нечем.
 */
const ContentWrapper = styled.div<{ $fluid?: boolean }>`
  isolation: isolate;
  width: 100%;
  max-width: ${({ $fluid }) => ($fluid ? 'none' : `${PAGE_MAX}px`)};
  box-sizing: border-box;
  flex: ${({ $fluid }) => ($fluid ? '1 1 auto' : '0 1 auto')};
  min-height: ${({ $fluid }) => ($fluid ? '0' : 'auto')};
  min-width: 0;
  display: flex;
  margin: 0 auto;
  flex-direction: column;
  /* Fluid pages must fill the width — a flex-start child would shrink to its
   * content, so a narrow view (e.g. the messenger's thread) would collapse the
   * whole page. Centered pages keep the start alignment. */
  align-items: ${({ $fluid }) => ($fluid ? 'stretch' : 'flex-start')};
  justify-content: flex-start;
  background-color: ${Colors.Transparent};

  ${NARROW_GUTTER} {
    padding: ${({ $fluid }) => ($fluid ? '0' : `0 ${PAGE_GUTTER}px`)};
  }
`;
